/** Site Visits panel — structured visit register. */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, MapPin, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listCPProfiles, listCPSiteVisits, addCPSiteVisit } from "@/lib/api";
import { CPField, CPTextInput, CPTextArea, CPNumberInput } from "@/pages/wizard/cp/_cpShared";


export default function CPSiteVisitsPanel({ projectId, summary, onRefresh }) {
  const qc = useQueryClient();
  const visitsQ = useQuery({
    queryKey: ["cp-visits", projectId],
    queryFn:  () => listCPSiteVisits(projectId),
    enabled:  !!projectId,
  });
  const profilesQ = useQuery({
    queryKey: ["cp-profiles", projectId],
    queryFn:  () => listCPProfiles(projectId),
    enabled:  !!projectId,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cp_profile_id: "", site: "", country: "", arrival_date: "", departure_date: "",
    purpose: "", drill_sites_visited: true, core_shed_visited: true, lab_visited: false,
    samples_observed: null, key_observations: "", limitations: "",
  });

  const add = useMutation({
    mutationFn: () => addCPSiteVisit(projectId, form),
    onSuccess:  () => {
      toast.success("Site visit logged");
      qc.invalidateQueries({ queryKey: ["cp-visits", projectId] });
      onRefresh?.();
      setOpen(false);
      setForm({
        cp_profile_id: "", site: "", country: "", arrival_date: "", departure_date: "",
        purpose: "", drill_sites_visited: true, core_shed_visited: true, lab_visited: false,
        samples_observed: null, key_observations: "", limitations: "",
      });
    },
    onError: (e) => toast.error(e.message),
  });

  const profiles = profilesQ.data?.profiles || [];
  const profileById = (id) => profiles.find((p) => p.id === id);
  const rows = visitsQ.data?.visits || [];
  const last = visitsQ.data?.last_visit;
  const daysSince = visitsQ.data?.days_since_last_visit;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {rows.length} site visit(s){last && ` · last ${daysSince ?? "—"} days ago`}
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    disabled={profiles.length === 0}
                    data-testid="w-cp-visit-new">
          <Plus size={11} className="mr-1" /> Log site visit
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="border-l-2 border-amber-400 bg-amber-50 p-2 text-[11px]">
          No site visits recorded. The declaration must explain any absence of a visit.
        </div>
      )}

      {rows.map((v) => (
        <div key={v.id} className="border border-slate-200 rounded p-3 bg-white"
              data-testid={`w-cp-visit-${v.id}`}>
          <div className="flex items-start gap-3">
            <MapPin size={16} className="text-slate-500 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <b className="text-[13px] text-slate-800">Visit {v.visit_number}: {v.site || "—"}, {v.country || "—"}</b>
                <span className="text-[10px] text-slate-500">by {profileById(v.cp_profile_id)?.full_name || "—"}</span>
                <span className="ml-auto text-[10px] font-mono text-slate-500">
                  <CalendarBlank size={11} className="inline mr-1 -mt-0.5" />
                  {v.arrival_date} → {v.departure_date} ({v.duration_days} day{v.duration_days === 1 ? "" : "s"})
                </span>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">{v.purpose || "—"}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {v.drill_sites_visited && <Tag>Drill sites</Tag>}
                {v.core_shed_visited && <Tag>Core shed</Tag>}
                {v.lab_visited && <Tag>Lab</Tag>}
                {v.sample_prep_visited && <Tag>Sample prep</Tag>}
                {v.mining_area_visited && <Tag>Mining</Tag>}
                {v.processing_visited && <Tag>Processing</Tag>}
                {v.stockpiles_visited && <Tag>Stockpiles</Tag>}
                {v.samples_observed && <Tag>{v.samples_observed} samples observed</Tag>}
              </div>
              {v.key_observations && (
                <div className="text-[11px] text-slate-700 mt-2 border-l-2 border-slate-300 pl-2">
                  <b>Key observations:</b> {v.key_observations}
                </div>
              )}
              {v.limitations && (
                <div className="text-[11px] text-amber-700 mt-1">
                  <b>Limitations:</b> {v.limitations}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log Competent Person site visit</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CPField label="CP">
              <Select value={form.cp_profile_id}
                        onValueChange={(v) => setForm({ ...form, cp_profile_id: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-visit-cp">
                  <SelectValue placeholder="Select CP" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Site">
              <CPTextInput value={form.site} onChange={(v) => setForm({ ...form, site: v })}
                             placeholder="Chunya" testId="w-cp-visit-site" />
            </CPField>
            <CPField label="Country">
              <CPTextInput value={form.country} onChange={(v) => setForm({ ...form, country: v })}
                             placeholder="Tanzania" testId="w-cp-visit-country" />
            </CPField>
            <CPField label="Arrival date">
              <CPTextInput type="date" value={form.arrival_date}
                             onChange={(v) => setForm({ ...form, arrival_date: v })}
                             testId="w-cp-visit-arrival" />
            </CPField>
            <CPField label="Departure date">
              <CPTextInput type="date" value={form.departure_date}
                             onChange={(v) => setForm({ ...form, departure_date: v })}
                             testId="w-cp-visit-departure" />
            </CPField>
            <CPField label="Samples observed">
              <CPNumberInput value={form.samples_observed}
                                onChange={(v) => setForm({ ...form, samples_observed: v })}
                                testId="w-cp-visit-samples" />
            </CPField>
            <CPField label="Purpose" span={2}>
              <CPTextArea value={form.purpose} rows={2}
                             onChange={(v) => setForm({ ...form, purpose: v })}
                             placeholder="Site inspection for CPR sign-off"
                             testId="w-cp-visit-purpose" />
            </CPField>
            <CPField label="Key observations" span={2}>
              <CPTextArea value={form.key_observations} rows={3}
                             onChange={(v) => setForm({ ...form, key_observations: v })}
                             placeholder="Core condition, QA/QC compliance, sample handling…"
                             testId="w-cp-visit-obs" />
            </CPField>
            <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                ["drill_sites_visited", "Drill sites"],
                ["core_shed_visited",   "Core shed"],
                ["lab_visited",         "Laboratory"],
                ["sample_prep_visited", "Sample prep"],
                ["mining_area_visited", "Mining area"],
                ["processing_visited",  "Processing"],
                ["stockpiles_visited",  "Stockpiles"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-[11px] font-mono">
                  <input type="checkbox" checked={!!form[k]}
                          onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={!form.cp_profile_id || !form.arrival_date || !form.departure_date || !form.purpose || add.isPending}
                       onClick={() => add.mutate()}
                       data-testid="w-cp-visit-save">
              {add.isPending ? "Saving…" : "Save visit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
      {children}
    </span>
  );
}
